import React from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { VIDEO_CREDIT_COST } from 'fym-shared/src/music24/music24Plans';
import type { SerializedDirectedVideo } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { DIRECTED_WIZARD_STEPS, directedScreenOf, directedStepIndex } from '../directedSteps';
import { directedMessages } from '../messages/directedMessages';
import { entryMessages as messages } from '../messages/entryMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DirectedVideoEntryProps {
  /** The open directed video of the song, or null: the button starts a new one. */
  open: SerializedDirectedVideo | null;
  /** The demo artist: the button is off (design a1 note). */
  readOnly: boolean;
  onStart: () => void;
  /** The new video button is the primary action of its card (the Kit tab music video card). */
  primary?: boolean;
  /** The plan makes no AI shots (Free, the trial): the button opens the plan offer. */
  needsPlan?: boolean;
}

const sparkle = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" strokeLinejoin="round" />
  </svg>
);

const EDIT_LINES: Readonly<Record<string, MessageDescriptor>> = {
  cast: messages.runCast,
  stories: messages.runStories,
  style: messages.runStyle,
  price: messages.runPrice,
};

/**
 * The directed video in the music video field of the song page (design a1, a2): the "Directed
 * video · Beta" button with its hint; while a video is edited, its step and "Continue"; while it is
 * made, the shots done and "See the progress".
 */
const DirectedVideoEntryComponent: React.FC<DirectedVideoEntryProps> = ({
  open,
  readOnly,
  onStart,
  primary = false,
  needsPlan = false,
}) => {
  const intl = useIntl();
  const beta = <span className={styles.betaOutline}>{intl.formatMessage(directedMessages.beta)}</span>;

  if (open === null) {
    return (
      <>
        <SMLButton
          variant={primary ? 'primary' : 'default'}
          className={styles.entryButton}
          icon={sparkle}
          disabled={readOnly}
          onClick={onStart}
        >
          {intl.formatMessage(messages.button)}
          {/* QA finding F21: on the purple button the badge takes the text colour of the button. */}
          <span className={styles.betaOnButton}>{intl.formatMessage(directedMessages.beta)}</span>
        </SMLButton>
        <span className={styles.hint}>{intl.formatMessage(messages.hintWhat)}</span>
        <span className={styles.hint} data-testid={needsPlan ? 'directed-entry-needs-plan' : undefined}>
          {needsPlan
            ? intl.formatMessage(messages.hintNeedsPlan)
            : intl.formatMessage(messages.hintPrice, { credits: VIDEO_CREDIT_COST.aiClip })}
        </span>
      </>
    );
  }

  // A ready video (design a2 "Ready"): the field above shows the cut and "Edit the video".
  if (open.status === 'ready') {
    return (
      <div className={styles.run} data-testid="directed-entry-ready">
        <div className={styles.runHead}>
          {intl.formatMessage(messages.button)}
          {beta}
          <span className={styles.grow} />
          <span className={styles.runLine}>{intl.formatMessage(messages.ready)}</span>
        </div>
        <span className={styles.runLine}>{intl.formatMessage(messages.readyLine)}</span>
      </div>
    );
  }

  const screen = directedScreenOf(open);
  if (screen === 'progress') {
    const { progress } = open;
    const isRendering = open.status === 'rendering';
    const done = progress?.clipsDone ?? 0;
    const total = progress?.clipsTotal ?? 0;
    const share = total > 0 ? Math.round((done / total) * 100) : 0;
    const line = intl.formatMessage(isRendering ? messages.rendering : messages.makingShots);
    const etaSec = progress?.etaSec ?? null;
    return (
      <div className={styles.run} data-testid="directed-entry-making">
        <div className={styles.runHead}>
          {intl.formatMessage(messages.button)}
          {beta}
          <span className={styles.grow} />
          {total > 0 && (
            <span className={styles.runLine}>{intl.formatMessage(messages.makingCount, { done, total })}</span>
          )}
        </div>
        <div className={styles.progressBar}>
          <i style={{ width: `${isRendering ? 100 : share}%` }} />
        </div>
        <span className={styles.runLine}>
          {etaSec === null
            ? line
            : intl.formatMessage(messages.eta, { line, minutes: Math.max(1, Math.round(etaSec / 60)) })}
        </span>
        <SMLButton className={styles.runAction} onClick={onStart}>
          {intl.formatMessage(messages.seeProgress)}
        </SMLButton>
      </div>
    );
  }

  let line: string;
  if (screen === 'script') {
    line =
      open.script === null
        ? intl.formatMessage(messages.runScriptWorking)
        : intl.formatMessage(messages.runScript, { count: open.script.shots.length });
  } else {
    line = intl.formatMessage(EDIT_LINES[screen] ?? messages.runCast);
  }
  return (
    <div className={styles.run} data-testid="directed-entry-run">
      <div className={styles.runHead}>
        {intl.formatMessage(messages.button)}
        {beta}
        <span className={styles.grow} />
        <span className={styles.runLine}>
          {intl.formatMessage(messages.runStep, {
            number: directedStepIndex(screen) + 1,
            count: DIRECTED_WIZARD_STEPS.length,
          })}
        </span>
      </div>
      <span className={styles.runLine}>{line}</span>
      <SMLButton variant="primary" className={styles.runAction} onClick={onStart}>
        {intl.formatMessage(messages.continue)}
      </SMLButton>
    </div>
  );
};

export const DirectedVideoEntry = TypedMemo(DirectedVideoEntryComponent);
